// Lightweight notification sounds via the Web Audio API — no asset files / CORS.
// The AudioContext is created lazily and resumed on a user gesture (browsers
// suspend audio until the user has interacted; sign-in / toggling sound counts).

let ctx: AudioContext | null = null;
let lastPing = 0;

function getCtx(): AudioContext | null {
  try {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    if (!ctx) ctx = new AC();
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
  } catch { return null; }
}

// Call on a user gesture to unlock audio for later (auto-)playback.
export function unlockAudio() { try { getCtx(); } catch { /* ignore */ } }

type ToneOpts = { type?: OscillatorType; dur?: number; vol?: number; gap?: number };

// Play a sequence of notes. Fully wrapped so audio can never break the UI.
function tone(notes: number[], opts: ToneOpts = {}): void {
  try {
    const ac = getCtx();
    if (!ac || ac.state !== 'running') return;
    const { type = 'sine', dur = 0.18, vol = 0.06, gap = 0.0 } = opts;
    const t0 = ac.currentTime;
    notes.forEach((f, i) => {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = type;
      osc.frequency.value = f;
      const start = t0 + i * gap;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.linearRampToValueAtTime(vol, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      osc.connect(gain); gain.connect(ac.destination);
      osc.start(start); osc.stop(start + dur + 0.03);
    });
  } catch { /* ignore */ }
}

// ---- named cues ----

// Screener surge — short chime; pitch/volume scale with surge strength. Throttled.
export function playPing(strength = 0.5): void {
  const now = Date.now();
  if (now - lastPing < 350) return;
  lastPing = now;
  const s = Math.max(0, Math.min(1, strength));
  const base = 640 + s * 260;
  tone([base, base * 1.5], { type: 'sine', dur: 0.2, vol: 0.05 + s * 0.05, gap: 0.09 });
}

// Order placed (resting / accepted) — neutral blip.
export function playPlaced(): void { tone([520], { type: 'triangle', dur: 0.1, vol: 0.05 }); }

// Entry filled — positive two-note up.
export function playFill(): void { tone([659, 880], { type: 'sine', dur: 0.17, vol: 0.075, gap: 0.075 }); }

// Target / take-profit filled — ascending major triad (cha-ching).
export function playProfit(): void { tone([659, 784, 1047], { type: 'triangle', dur: 0.2, vol: 0.075, gap: 0.085 }); }

// Stop filled / stopped out — lower descending alert.
export function playStop(): void { tone([392, 294], { type: 'sawtooth', dur: 0.22, vol: 0.06, gap: 0.1 }); }

// Order cancelled / rejected — short low blip down.
export function playCancel(): void { tone([392, 277], { type: 'triangle', dur: 0.12, vol: 0.045, gap: 0.06 }); }

// Ladder signal on a WATCHLIST symbol (fired even when its card isn't open).
// Mirrors LadderCard's blip: bullish flip → rising sine; bearish veto → low square.
export function playLadderAlarm(bullish: boolean): void {
  if (bullish) tone([660, 990], { type: 'sine', dur: 0.16, vol: 0.09, gap: 0.09 });
  else tone([220, 180], { type: 'square', dur: 0.24, vol: 0.05, gap: 0.12 });
}

// Heat rotation — a symbol rotated into the hot set (HeatStrip). Short two-tone
// up beep (D5→A5 triangle), quieter than a fill so it reads as ambient radar
// rather than an order event.
export function playRotation(): void {
  tone([587, 880], { type: 'triangle', dur: 0.14, vol: 0.06, gap: 0.09 });
}
